import { Trans, useLingui } from '@lingui/react/macro'

import { usePermissions } from '$src/lib/hooks/usePermissions'
import { usePropertyCreation } from '$src/lib/hooks/usePropertyCreation'
import { billingHooks, customerHooks } from '$src/lib/trpcHooks'
import {
  Badge,
  Button,
  Card,
  Collapse,
  Divider,
  Group,
  Stack,
  Text,
  TextInput,
} from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { modals } from '@mantine/modals'
import { CustomerWithRelations } from '@markku/types'
import { AddPropertyModal } from './AddPropertyModal'
import {
  IconBuildingCommunity,
  IconChevronDown,
  IconChevronRight,
  IconChevronUp,
  IconCreditCard,
  IconEdit,
  IconPlus,
  IconUsers,
} from '@tabler/icons-react'
import { Link } from '@tanstack/react-router'
import { useState } from 'react'

interface CustomerCardProps {
  customer: CustomerWithRelations
}

export function CustomerCard({ customer }: CustomerCardProps) {
  const { t } = useLingui()
  const [membersOpened, { toggle: toggleMembers }] = useDisclosure(false)
  const [
    propertyModalOpened,
    { open: openPropertyModal, close: closePropertyModal },
  ] = useDisclosure(false)
  const { handlePropertyCreated, WelcomeModal } = usePropertyCreation()
  const { mutate: getBillingPortalUrl, isPending: isBillingPending } =
    billingHooks.useGetBillingPortalUrl()
  const { isOwnerOf } = usePermissions()

  const handlePropertyCreatedAndCloseModal = (
    propertyId: number,
    propertyAddress: string,
  ) => {
    closePropertyModal()
    handlePropertyCreated(propertyId, propertyAddress)
  }

  const isOwner = isOwnerOf(customer.id)
  const hasStripeCustomer = !!customer.stripeCustomerId
  const canAccessBilling = isOwner && hasStripeCustomer

  const handleBillingPortal = () => {
    getBillingPortalUrl({ customerId: customer.id })
  }

  const handleEdit = () => {
    modals.open({
      title: t`Edit customer`,
      centered: true,
      children: <EditCustomerNameForm customer={customer} />,
    })
  }

  return (
    <Card radius="md" withBorder>
      <Stack gap="md">
        {/* Customer Header */}
        <Group justify="space-between" align="flex-start">
          <Group gap="sm" style={{ flex: 1 }}>
            <IconBuildingCommunity size={20} />
            <Text fw={600}>{customer.name}</Text>
            {isOwner && (
              <Button
                variant="light"
                size="compact-xs"
                onClick={handleEdit}
                p={4}
              >
                <IconEdit size={16} />
              </Button>
            )}
          </Group>
          <Badge
            variant="light"
            color={isOwner ? 'brandOrange' : 'gray'}
            style={{ flexShrink: 0 }}
          >
            {isOwner ? <Trans>Owner</Trans> : <Trans>Member</Trans>}
          </Badge>
        </Group>

        <Divider />

        {/* Properties Section */}
        <Stack gap="sm">
          <Group justify="space-between">
            <Text size="sm" fw={500} c="dimmed">
              <Trans>Properties</Trans>
            </Text>
            {isOwner && (
              <Button
                variant="light"
                size="compact-sm"
                onClick={openPropertyModal}
                leftSection={<IconPlus size={16} />}
              >
                <Trans>Add property</Trans>
              </Button>
            )}
          </Group>

          {customer.properties?.length ? (
            <Stack gap="xs">
              {customer.properties.map((property) => {
                const activeSubscription = property.subscriptions?.find(
                  (sub) => sub.status === 'active',
                )

                return (
                  <Card
                    key={property.id}
                    component={Link}
                    to={`/properties/${property.id}`}
                    withBorder
                    radius="sm"
                    p="sm"
                    style={{
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                    }}
                    styles={{
                      root: {
                        '&:hover': {
                          transform: 'translateX(4px)',
                          borderColor: 'var(--mantine-color-brandOrange-3)',
                        },
                      },
                    }}
                  >
                    <Group justify="space-between" wrap="nowrap">
                      <Stack gap={4} style={{ flex: 1 }}>
                        <Text size="sm" fw={500}>
                          {property.address?.street}, {property.address?.city}
                        </Text>
                        {activeSubscription ? (
                          <Group gap={4}>
                            <Badge size="xs" variant="light" color="green">
                              {activeSubscription.displayName}
                            </Badge>
                            <Group gap={2}>
                              {activeSubscription.effectivePriceMonthly &&
                                activeSubscription.effectivePriceMonthly !==
                                  activeSubscription.basePriceMonthly && (
                                  <Text size="xs" c="dimmed" td="line-through">
                                    {activeSubscription.basePriceMonthly}€
                                  </Text>
                                )}
                              <Text size="xs" c="dimmed">
                                {activeSubscription.effectivePriceMonthly ||
                                  activeSubscription.basePriceMonthly}
                                <Trans>€/mo</Trans>
                              </Text>
                            </Group>
                          </Group>
                        ) : (
                          <Text size="xs" c="dimmed">
                            <Trans>No active subscription</Trans>
                          </Text>
                        )}
                      </Stack>
                      <IconChevronRight
                        size={16}
                        color="var(--mantine-color-gray-5)"
                      />
                    </Group>
                  </Card>
                )
              })}
            </Stack>
          ) : (
            <Text size="sm" c="dimmed" ta="center">
              <Trans>No properties</Trans>
            </Text>
          )}
        </Stack>

        {/* Members Section */}
        {customer.members?.length && (
          <>
            <Divider />
            <Stack gap="xs">
              <Group
                justify="space-between"
                onClick={toggleMembers}
                style={{ cursor: 'pointer' }}
              >
                <Group gap="xs">
                  <IconUsers size={16} color="var(--mantine-color-gray-6)" />
                  <Text size="sm" fw={500} c="dimmed">
                    <Trans>Members</Trans> ({customer.members.length})
                  </Text>
                </Group>
                {membersOpened ? (
                  <IconChevronUp size={16} />
                ) : (
                  <IconChevronDown size={16} />
                )}
              </Group>

              <Collapse in={membersOpened}>
                <Stack gap="xs" mt="xs">
                  {customer.members.map((member) => (
                    <Card key={member.id} withBorder radius="sm" p="xs">
                      <Group justify="space-between">
                        <Stack gap={0}>
                          <Text size="sm" fw={500}>
                            {member.user?.firstName} {member.user?.lastName}
                          </Text>
                          <Text size="xs" c="dimmed">
                            {member.user?.email}
                          </Text>
                        </Stack>
                        <Badge
                          size="xs"
                          variant="light"
                          color={
                            member.role === 'owner' ? 'brandOrange' : 'gray'
                          }
                        >
                          {member.role === 'owner' ? (
                            <Trans>Owner</Trans>
                          ) : (
                            <Trans>Member</Trans>
                          )}
                        </Badge>
                      </Group>
                    </Card>
                  ))}
                </Stack>
              </Collapse>
            </Stack>
          </>
        )}

        {/* Billing Section */}
        {canAccessBilling && (
          <>
            <Divider />
            <Button
              leftSection={<IconCreditCard size={16} />}
              variant="light"
              onClick={handleBillingPortal}
              loading={isBillingPending}
            >
              <Trans>Manage payment method and invoices</Trans>
            </Button>
          </>
        )}
      </Stack>

      {/* Add Property Modal */}
      <AddPropertyModal
        opened={propertyModalOpened}
        onClose={closePropertyModal}
        customerId={customer.id}
        onPropertyCreated={handlePropertyCreatedAndCloseModal}
      />

      {WelcomeModal}
    </Card>
  )
}

function EditCustomerNameForm({
  customer,
}: {
  customer: CustomerWithRelations
}) {
  const { t } = useLingui()
  const [name, setName] = useState(customer.name || '')
  const { mutateAsync: updateName, isPending } = customerHooks.useUpdateName()

  const handleSubmit = async () => {
    if (!name.trim()) {
      return
    }

    await updateName({
      id: customer.id,
      data: {
        name: name.trim(),
      },
    })
    modals.closeAll()
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !isPending) {
      handleSubmit()
    }
  }

  return (
    <Stack gap="md">
      <TextInput
        label={t`Customer name`}
        value={name}
        onChange={(e) => setName(e.currentTarget.value)}
        onKeyDown={handleKeyDown}
        required
      />
      <Button onClick={handleSubmit} loading={isPending}>
        <Trans>Save</Trans>
      </Button>
    </Stack>
  )
}
